<template>
  <!-- Sidebar tools: OptIn Panel, Help, Account -->
  <div
    ref="sidebarToolsRef"
    class="p-3 pb-6 space-y-4"
  >
    <!-- OptIn Panel -->
    <AM3OptInPanel class="mb-4 ml-0.5" />

    <!-- Tasks dropdown -->
    <div class="flex items-center mb-4">
      <div
        :id="tasksDropdownId"
        v-tooltip.right="{
          value: isCollapsed ? `Tasks${formattedPendingTasks?.length ? ` (${formattedPendingTasks.length})` : ''}` : null,
          hideDelay: 100,
          showDelay: 500,
        }"
        class="flex items-center !cursor-pointer"
        data-test-id="pending-task-sidebar-dropdown"
        :aria-label="`View tasks${formattedPendingTasks?.length ? ` (${formattedPendingTasks.length} running)` : ''}`"
        role="button"
        :aria-expanded="isTasksDropdownOpen"
        @click.stop="toggleTasksDropdown"
      >
        <div
          class="navbar-item flex-shrink-0 rounded-full bg-surface flex items-center justify-center size-10 text-primary hover:bg-surfaces-neutral"
          @mouseenter="tasksIconHovered = true"
          @mouseleave="tasksIconHovered = false"
        >
          <AM3Icon
            type="arrow-path"
            class="w-5 h-5 navbar-icon"
            :color="tasksIconHovered ? 'brand' : undefined"
            :class="{ 'animate-spin': isRunningTaskAnimation || (formattedPendingTasks && formattedPendingTasks.length > 0) }"
          />
        </div>
        <span
          v-if="isMobile && !isCollapsed"
          class="ml-3 text-sm font-medium text-secondary"
        >
          Tasks{{ formattedPendingTasks?.length ? ` (${formattedPendingTasks.length})` : '' }}
        </span>
      </div>

      <!-- Tasks dropdown panel -->
      <AM3DropdownPanel
        :target="tasksDropdownId"
        :model-value="isTasksDropdownOpen"
        position="top-right"
        fit-content
        @update:model-value="isTasksDropdownOpen = $event"
      >
        <div
          class="w-full max-h-[90vh] overflow-auto"
          :class="{ 'min-w-[320px]': isMobile, 'min-w-[480px]': !isMobile }"
        >
          <!-- Tasks header -->
          <div class="px-4 py-3 border-surface-dark flex items-center justify-between">
            <span class="font-bold">
              Currently running tasks{{ formattedPendingTasks?.length ? ` (${formattedPendingTasks.length})` : '' }}
            </span>
            <button
              class="text-secondary hover:text-brand"
              @click="isTasksDropdownOpen = false"
            >
              <AM3Icon
                type="x-mark"
                class="w-4 h-4"
              />
            </button>
          </div>

          <!-- No tasks message -->
          <div
            v-if="!formattedPendingTasks?.length"
            class="px-4 py-3 text-sm text-secondary"
          >
            No tasks running
          </div>

          <!-- Tasks list -->
          <div v-else>
            <div
              v-for="(task, index) in formattedPendingTasks"
              :key="index"
              class="px-4 py-3 border-b border-surface-dark hover:bg-surfaces-neutral flex items-center cursor-pointer"
              @click="handlePendingTaskItemSelect(task)"
            >
              <AM3Icon
                v-if="task.icon"
                :type="task.icon"
                class="w-5 h-5 mr-3 text-secondary"
              />
              <div>
                <div class="font-medium">
                  {{ task.name }}
                </div>
                <div
                  v-if="task.subtitle || task.started"
                  class="text-xs text-secondary"
                >
                  {{ task.subtitle }} {{ task.started ? `- ${task.started}` : '' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Completed tasks -->
          <div v-if="formattedCompletedTasks?.length">
            <div class="px-4 py-3 border-surface-dark font-bold">
              Recently completed tasks ({{ formattedCompletedTasks.length }})
            </div>

            <div
              v-for="(task, index) in formattedCompletedTasks"
              :key="index"
              class="px-4 py-3 border-b border-surface-dark hover:bg-surfaces-neutral flex items-center cursor-pointer"
              @click="handlePendingTaskItemSelect(task)"
            >
              <AM3Icon
                v-if="task.icon"
                :type="task.icon"
                class="w-5 h-5 mr-3 text-secondary"
              />
              <div>
                <div class="font-medium">
                  {{ task.name }}
                </div>
                <div
                  v-if="task.subtitle || task.started"
                  class="text-xs text-secondary"
                >
                  {{ task.subtitle }} {{ task.started ? `- ${task.started}` : '' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AM3DropdownPanel>
    </div>

    <!-- Help button -->
    <div
      id="open-intercom-button"
      v-tooltip.right="{
        value: isCollapsed ? 'Help' : null,
        hideDelay: 100,
        showDelay: 500,
      }"
      class="flex items-center cursor-pointer mb-4"
      data-test-id="open-intercom-button"
      role="button"
      aria-label="Open help and support"
      @click.stop="arEnableAudienceAI ? openAIPanel('support') : toggleIntercom()"
    >
      <div
        class="navbar-item flex-shrink-0 rounded-full flex items-center justify-center size-10 text-primary "
        :class="intercomVisible ? 'intercom-visible bg-brand text-inverse' : 'bg-surface hover:bg-surfaces-neutral' "
        @mouseenter="helpIconHovered = true"
        @mouseleave="helpIconHovered = false"
      >
        <AM3Icon
          type="question-mark-circle"
          class="navbar-icon w-5 h-5"
          :color="helpIconHovered ? 'brand' : 'secondary'"
        />
      </div>
      <span
        v-if="isMobile && !isCollapsed"
        class="ml-3 text-sm font-medium text-secondary"
      >Help</span>
    </div>

    <!-- Account dropdown -->
    <div
      :id="accountDropdownId"
      class="flex items-center cursor-pointer"
      data-test-id="sidebar-profile-avatar"
      role="button"
      :aria-label="`Account menu for ${accountFirstName} ${accountLastName}`"
      :aria-expanded="isAccountDropdownOpen"
      @click.stop="toggleAccountDropdown"
    >
      <div
        class="flex-shrink-0 rounded-full hover:bg-surfaces-neutral navbar-item"
        @mouseenter="accountIconHovered = true"
        @mouseleave="accountIconHovered = false"
      >
        <AM3Avatar
          v-tooltip="{
            value: isCollapsed ? 'Account' : null,
            hideDelay: 100,
            showDelay: 500,
          }"
          :name="`${accountFirstName} ${accountLastName}`"
          size="sm"
          :border-color="accountIconHovered ? 'brand' : undefined"
          class="account-avatar flex-shrink-0"
        />
      </div>
      <span
        v-if="isMobile && !isCollapsed"
        class="ml-3 text-sm font-medium text-secondary truncate"
      >
        {{ isMobile ? `${accountFirstName} ${accountLastName}` : accountFirstName }}
      </span>
    </div>

    <!-- Account dropdown panel -->
    <AM3DropdownPanel
      :target="accountDropdownId"
      :model-value="isAccountDropdownOpen"
      position="top-right"
      @update:model-value="isAccountDropdownOpen = $event"
    >
      <div class="w-full min-w-[240px]">
        <!-- Profile header -->
        <div class="px-6 py-4 border-b border-surface-dark flex items-start">
          <AM3Avatar
            :name="`${accountFirstName} ${accountLastName}`"
            size="md"
            class="flex-shrink-0 mr-4"
          />
          <div class="overflow-hidden max-w-[160px]">
            <div class="font-bold uppercase truncate">
              {{ `${accountFirstName} ${accountLastName}` }}
            </div>
            <div
              v-if="!isAdminAccount"
              class="text-xs text-secondary mt-1 truncate"
            >
              {{ accountEmailAddress }}
            </div>
          </div>
        </div>

        <!-- Menu items -->
        <div class="py-2">
          <div
            v-for="(item, index) in dropdownItems"
            :key="index"
          >
            <!-- Divider -->
            <div
              v-if="item.type === 'divider'"
              class="border-b border-surface-dark my-2"
            />

            <!-- Menu item -->
            <div
              v-else-if="item.type !== 'header' && item.type !== 'profile'"
              class="px-6 py-3 hover:bg-surfaces-neutral flex items-center cursor-pointer"
              :class="{ 'opacity-50 cursor-not-allowed': item.disabled }"
              @click="handleActionSelect(item)"
            >
              <AM3Icon
                v-if="item.icon?.name"
                :type="item.icon.name"
                class="mr-4 text-secondary w-5 h-5"
              />
              <span>{{ item.name }}</span>
              <span
                v-if="item.tag"
                :class="[
                  'ml-2 px-1 text-xs rounded',
                  item.tagColor ? '' : 'text-white',
                  item.tagBackground ? '' : 'bg-[#E39425]',
                ]"
                :style="item.tagColor || item.tagBackground ? {
                  color: item.tagColor || '#FFFFFF',
                  backgroundColor: item.tagBackground || '#E39425',
                } : {}"
              >
                {{ item.tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </AM3DropdownPanel>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import AM3OptInPanel from '@/components/am3/opt-in-panel/AM3OptInPanel.vue'
import { defaultSettingsPath } from '@/utils/helpers/index.js'
import { useTaskPolling } from '@/composables/useTaskPolling'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Avatar from '@/components/am3-generic/base/AM3Avatar.vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import { useAudienceAIPanel } from '@/composables/useAudienceAIPanel'

interface Props {
  isMobile: boolean
  isCollapsed: boolean
}

defineProps<Props>()

const store = useStore()
const router = useRouter()
const { open: openAIPanel } = useAudienceAIPanel()

// State
const isAccountDropdownOpen = ref(false)
const isTasksDropdownOpen = ref(false)
const accountDropdownId = ref(`account-dropdown-${uuidv4()}`)
const tasksDropdownId = ref(`tasks-dropdown-${uuidv4()}`)
const tasksIconHovered = ref(false)
const helpIconHovered = ref(false)
const accountIconHovered = ref(false)
const intercomVisible = ref(false)
const isRunningTaskAnimation = ref(false)

// Template ref
const sidebarToolsRef = ref<HTMLElement | null>(null)

// Computed properties
const account = computed(() => store.state.authModule.account || {})
const accountFirstName = computed(() => account.value?.firstName || '')
const accountLastName = computed(() => account.value?.lastName || '')
const accountEmailAddress = computed(() => account.value?.emailAddress || '')
const accountPromoterOid = computed(() => account.value?.promoterOid || null)
const isAdminAccount = computed(() => store.getters['authModule/isAdminAccount'])
const formattedPendingTasks = computed(() => store.getters['promoterTasksModule/formattedTasks'] || [])
const formattedCompletedTasks = computed(() => store.getters['promoterTasksModule/formattedCompletedTasks'] || [])
const config = useRuntimeConfig().public || {}

// Config values
const arEnableAudienceAI = config.arEnableAudienceAI
const arEnableCustomFields = computed(() => config.arEnableCustomFields)
const arEnablePrivacyPortal = computed(() => config.arEnablePrivacyPortal)

// Feature flags
const isFeatureEnabled = (keys: string[]) => store.getters['authModule/isFeatureEnabled'](keys)
const isCustomFieldsFeatureEnabled = computed(() => isFeatureEnabled(['custom-fields']))

// Dropdown items
const dropdownItems = computed(() => {
  if (isAdminAccount.value) {
    return [
      {
        name: 'Promoter Admin',
        action: () => navigateTo('/admin/dashboard'),
      },
      {
        type: 'divider',
      },
      {
        name: 'Log Out',
        action: performLogout,
      },
    ]
  }
  else {
    const items = [
      {
        icon: {
          name: 'cog-8-tooth',
        },
        name: 'Account settings',
        action: () => navigateTo(defaultSettingsPath(accountPromoterOid.value)),
      },
    ]

    if (arEnableCustomFields.value) {
      let customFieldMenuItem: any = {
        icon: {
          name: 'customfields',
        },
        name: 'Custom Fields',
        action: () => navigateTo('/custom-fields'),
      }

      if (!isCustomFieldsFeatureEnabled.value) {
        customFieldMenuItem = {
          ...customFieldMenuItem,
          tag: 'plus',
          tagColor: '#FFFFFF',
          tagBackground: '#E39425',
          tagIcon: 'logo-transparent',
          disabled: true,
        }
      }

      items.push(customFieldMenuItem)
    }

    if (arEnablePrivacyPortal.value) {
      items.push({
        icon: {
          name: 'shield-check',
        },
        name: 'Privacy Portal',
        action: () => navigateTo('/privacy-portal'),
      })
    }

    items.push(
      {
        type: 'divider',
      },
      {
        icon: {
          name: 'arrow-right-start-on-rectangle',
        },
        name: 'Sign Out',
        action: performLogout,
      },
    )

    return items
  }
})

// Methods
const toggleAccountDropdown = () => {
  isAccountDropdownOpen.value = !isAccountDropdownOpen.value
}

const toggleTasksDropdown = () => {
  isTasksDropdownOpen.value = !isTasksDropdownOpen.value

  // Trigger immediate poll when opening the tasks dropdown
  if (isTasksDropdownOpen.value) {
    const { refreshNow } = useTaskPolling()
    refreshNow()
  }
}

const navigateTo = (path: string) => {
  router.push({ path }).catch((error) => {
    // Ignore navigation duplication errors
    if (error.name !== 'NavigationDuplicated') {
      throw error
    }
  })
}

const performLogout = () => {
  store.dispatch('authModule/LOGOUT')
}

const handleActionSelect = (item: any) => {
  if (item.action && !item.disabled) {
    item.action()
    isAccountDropdownOpen.value = false
  }
}

const handlePendingTaskItemSelect = (task: any) => {
  if (task.clickthrough) {
    navigateTo(task.clickthrough)
    isTasksDropdownOpen.value = false
  }
}

const toggleIntercom = () => {
  if (window.Intercom) {
    if (intercomVisible.value) {
      window.Intercom('hide')
      intercomVisible.value = false
    }
    else {
      window.Intercom('show')
      intercomVisible.value = true
    }
  }
  else {
    console.error('Intercom is not initialized')
  }
}

// Watch for changes in completed tasks to animate icon
watch(formattedCompletedTasks, (newTasks, oldTasks) => {
  if (newTasks.length && oldTasks.length
    && newTasks[0]?.startTime !== oldTasks[0]?.startTime) {
    isRunningTaskAnimation.value = true
    setTimeout(() => {
      isRunningTaskAnimation.value = false
    }, 6000)
  }
})

// Expose sidebarToolsRef for parent component
defineExpose({
  sidebarToolsRef,
})
</script>

<style scoped>
:deep(.help-avatar),
:deep(.account-avatar) {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
}

:deep(.navbar-item:hover .navbar-icon svg) {
  color: var(--font-colour-brand);
}

:deep(.navbar-item.intercom-visible .navbar-icon svg) {
  color: var(--font-colour-white);
}

.navbar-item:hover :deep(.am3-avatar-initials) {
  color: var(--font-colour-brand) !important;
}
</style>
